// **Symbol mode**: the close tier's zoomed-out reading of bees and flowers.
//
// Sprites are world-anchored (`projection.ts`'s `spriteCameraScale`), which is what makes a
// zoom sweep read as one coherent world — but it has a floor of usefulness. Below roughly
// {@link SYMBOL_ZOOM_THRESHOLD} a bee's 24 px body is a handful of muddy pixels and a
// flower is fewer: the pixel art stops carrying information and starts carrying noise, and
// what a player surveying their colony's spread actually needs from that view is *where
// things are and what kind they are*, not what they look like.
//
// So below the threshold those two families draw as **colour symbols** instead — a dot per
// bee in its own body colour (the same `beeTintFor` palette the sprite bakes in, so type and
// enemy/own read identically in both modes) and a dot per flower patch in its variety colour.
// Everything else — the comb, the hive, the ground scatter — keeps its sprite or primitive,
// because those are large-footprint things that stay legible when a bee does not.
//
// This is **not** an LOD tier. The wire/trace tier is unchanged: the same close-tier records
// are on screen, drawn a different way. It is the same idea as the ground scatter's
// `scatterDetailForScale` cut and the patch meter's `meterLegibleAtScale` cut — a legibility
// decision that belongs to the camera, not to the data.

import type { AssetKey } from './assets';

/**
 * The camera scale at or below which bees and flower patches draw as colour symbols rather
 * than sprites. At this zoom a world-anchored bee sprite renders around 10 px and a flower
 * smaller; below it they degrade into speckle faster than they lose meaning, which is
 * exactly when a symbol beats a picture.
 */
export const SYMBOL_ZOOM_THRESHOLD = 0.8;

/**
 * Whether the camera is far enough out that bees and flowers should read as symbols.
 * **Monotone** in `scale` — like `scatterDetailForScale` and `beeBarsVisibleAtScale` — so a
 * continuous zoom sweep crosses the boundary exactly once and never flickers across it. A
 * degenerate camera (non-finite, zero, negative) reads as fully zoomed out.
 */
export function symbolModeForScale(scale: number): boolean {
  if (!Number.isFinite(scale) || scale <= 0) {
    return true;
  }
  return scale <= SYMBOL_ZOOM_THRESHOLD;
}

/** The drawable kinds symbol mode replaces. Everything else keeps its sprite. */
export function drawsAsSymbol(kind: string): boolean {
  return kind === 'bee' || kind === 'patch';
}

/** A dead patch's symbol: the ground-wax grey the dead-flower sheet reads as, so a drained
 * patch is obviously not a bloom without needing the sprite. */
const DEAD_FLOWER_SYMBOL = 0x8a7d5f;

/** Each live flower variety's symbol colour — the dominant hue of the sheet it stands in
 * for, so the field's colour variety survives the switch to symbols rather than flattening
 * into one anonymous dot colour. */
const FLOWER_SYMBOL_COLORS: Partial<Record<AssetKey, number>> = {
  flowerBlue: 0x5b8dd9,
  flowerPink: 0xe86fa8,
  flowerPurple: 0x9b6fd9,
  flowerRed: 0xd94f4f,
  flowerWhite: 0xf2efe4,
  flowerDead: DEAD_FLOWER_SYMBOL,
};

/** The symbol colour standing in for a flower sheet. */
export function flowerSymbolColor(sheet: AssetKey | undefined): number {
  return (sheet === undefined ? undefined : FLOWER_SYMBOL_COLORS[sheet]) ?? DEAD_FLOWER_SYMBOL;
}

/** A bee symbol's screen radius (px) at `scale`. Shrinks with the camera like everything
 * else, but floors at a pixel that still reads — the point of a symbol is to stay visible
 * where the sprite would not. */
export function beeSymbolRadius(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) {
    return BEE_SYMBOL_MIN_PX;
  }
  return Math.max(BEE_SYMBOL_MIN_PX, 4 * scale);
}

/** A flower symbol's screen radius (px) at `scale` — a touch larger than a bee's, matching
 * the patch's much larger world footprint. */
export function patchSymbolRadius(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) {
    return PATCH_SYMBOL_MIN_PX;
  }
  return Math.max(PATCH_SYMBOL_MIN_PX, 5 * scale);
}

const BEE_SYMBOL_MIN_PX = 1.5;
const PATCH_SYMBOL_MIN_PX = 2;
